import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { credentialsApi, workflowsApi } from '../services/api';
import { Key, Activity, Workflow, CheckCircle, Timer } from 'lucide-react';

interface Stats {
  totalCredentials: number;
  activeWorkflows: number;
  runs: number;
  successRate: number | null;
  timeSavedMinutes: number;
}

function formatTimeSaved(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return minutes % 60 ? `${hours}h ${minutes % 60}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  return hours % 24 ? `${days}d ${hours % 24}h` : `${days}d`;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState<Stats>({
    totalCredentials: 0,
    activeWorkflows: 0,
    runs: 0,
    successRate: null,
    timeSavedMinutes: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      const [credResult, overviewResult] = await Promise.allSettled([
        credentialsApi.getAll(),
        workflowsApi.getOverview(),
      ]);

      setStats((prev) => {
        const next = { ...prev };
        if (credResult.status === 'fulfilled') {
          next.totalCredentials = credResult.value.data.data.total || 0;
        } else {
          console.error('Failed to fetch credentials:', credResult.reason);
        }
        if (overviewResult.status === 'fulfilled') {
          const overview = overviewResult.value.data.data;
          next.activeWorkflows = overview.activeWorkflows || 0;
          next.runs = overview.runs || 0;
          next.successRate = overview.successRate;
          next.timeSavedMinutes = overview.timeSavedMinutes || 0;
        } else {
          console.error('Failed to fetch workflow overview:', overviewResult.reason);
        }
        return next;
      });
      setLoading(false);
    };

    fetchStats();
  }, []);

  return (
    <div>
      {/* Welcome section */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">
          Welcome back, {user?.first_name || user?.username}!
        </h2>
        <p className="text-gray-400">
          Here's an overview of your workflows and activity over the last 30 days.
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-orange-500/20 rounded-lg">
              <Workflow className="text-orange-400" size={24} />
            </div>
            <div className="ml-4">
              <p className="text-gray-400 text-sm">Published Workflows</p>
              <p className="text-2xl font-bold text-white">
                {loading ? '-' : stats.activeWorkflows}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-blue-500/20 rounded-lg">
              <Activity className="text-blue-400" size={24} />
            </div>
            <div className="ml-4">
              <p className="text-gray-400 text-sm">Runs (30 days)</p>
              <p className="text-2xl font-bold text-white">
                {loading ? '-' : stats.runs}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-green-500/20 rounded-lg">
              <CheckCircle className="text-green-400" size={24} />
            </div>
            <div className="ml-4">
              <p className="text-gray-400 text-sm">Success Rate</p>
              <p className="text-2xl font-bold text-white">
                {loading ? '-' : stats.successRate === null ? '—' : `${stats.successRate}%`}
              </p>
            </div>
          </div>
        </div>

        {stats.timeSavedMinutes > 0 ? (
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-teal-500/20 rounded-lg">
                <Timer className="text-teal-400" size={24} />
              </div>
              <div className="ml-4">
                <p className="text-gray-400 text-sm">Time Saved (30 days)</p>
                <p className="text-2xl font-bold text-white">
                  {formatTimeSaved(stats.timeSavedMinutes)}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-purple-500/20 rounded-lg">
                <Key className="text-purple-400" size={24} />
              </div>
              <div className="ml-4">
                <p className="text-gray-400 text-sm">Credentials</p>
                <p className="text-2xl font-bold text-white">
                  {loading ? '-' : stats.totalCredentials}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick actions */}
      <div className="card">
        <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link
            to="/workflows"
            className="flex items-center p-4 bg-gray-700/50 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <Workflow className="text-brand-orange" size={24} />
            <div className="ml-4">
              <p className="text-white font-medium">View Workflows</p>
              <p className="text-gray-400 text-sm">See run history for your automations</p>
            </div>
          </Link>

          <Link
            to="/credentials"
            className="flex items-center p-4 bg-gray-700/50 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <Key className="text-brand-orange" size={24} />
            <div className="ml-4">
              <p className="text-white font-medium">View Credentials</p>
              <p className="text-gray-400 text-sm">See all your stored credentials</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Info box */}
      <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
        <h4 className="text-blue-400 font-medium mb-2">Read-Only Access</h4>
        <p className="text-gray-300 text-sm">
          This portal provides read-only access to your workflows, run history, and stored
          credentials. Sensitive data is masked for security. To request changes, contact
          PHH Digital.
        </p>
      </div>
    </div>
  );
}
